import { SCOPE_PRIORITY, isEditableTarget } from '@/shared/config/shortcuts';
import { matchesCombo } from '@/shared/lib/keyCombo';
import { type ResolvedShortcut, useShortcutStore } from '@/shared/stores/shortcutStore';
import { useEffect } from 'react';

/**
 * The single shortcut surface, mounted once at the app root beside the dialog
 * and toast surfaces. It owns the one `window` `keydown` listener for the whole
 * app: on each keydown it reads the current bindings from {@link useShortcutStore}
 * (via `getState()`, so the one listener stays correct as bindings register and
 * unregister without re-attaching) and dispatches to a single winner.
 *
 * Resolution is scoped and per-combo: among the bindings whose combo matches, the
 * winner is the one with the highest scope priority (ties broken toward the
 * most-recently-registered). A higher scope therefore shadows only the *same*
 * combo in lower scopes — combos the higher scope doesn't bind still fall
 * through. A keydown from an editable element is ignored unless the winning
 * command sets `allowInInput`; `preventDefault` is honored per command.
 */
export function ShortcutHost() {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const { shortcuts } = useShortcutStore.getState();
      const editable = isEditableTarget(event.target);

      let winner: ResolvedShortcut | null = null;
      for (const binding of Object.values(shortcuts)) {
        if (!matchesCombo(event, binding.keys)) continue;
        if (winner === null || outranks(binding, winner)) winner = binding;
      }

      if (winner === null) return;
      if (editable && !winner.allowInInput) return;
      if (winner.preventDefault) event.preventDefault();
      winner.handler(event);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return null;
}

/** True when `candidate` should beat `current`: higher scope, or same scope but registered later. */
function outranks(candidate: ResolvedShortcut, current: ResolvedShortcut): boolean {
  const byScope = SCOPE_PRIORITY[candidate.scope] - SCOPE_PRIORITY[current.scope];
  return byScope > 0 || (byScope === 0 && candidate.seq > current.seq);
}
